import React from "react";
import { observer } from "mobx-react";
import { Alert, Box, Grid2, IconButton, Paper, useTheme } from "@mui/material";
import {
  CheckCircleOutline,
  Close,
  CloudUploadOutlined,
  DescriptionOutlined,
  DownloadOutlined,
  ErrorOutline,
  FileUploadOutlined,
  InfoOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import ImageUpload from "../../../../../views/components/common/upload/ImageUpload";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import {
  formatPodOrderCsvImportIssue,
  getPodOrderCsvImportIssues,
  parsePodOrderCsvImportResult,
} from "../../../../../models/PodOrder/PodOrder";
import { APIResponse } from "../../../../../models/NetworkResponse";
import { POD_PRINTING_TYPE_DEFINITIONS } from "../../../../../models/PodOrder/PodPrintingType";
import { PodOrderPrintingTypeBadge } from "../../components/pod-order/podOrderBadges";

const POD_ALLOWED_PRINTING_TYPES = POD_PRINTING_TYPE_DEFINITIONS.filter(
  (item) =>
    item.name === "DTF" ||
    item.name === "UV Print" ||
    item.name === "LASER" ||
    item.name === "EMBROIDERY" ||
    item.name === "STICKER",
);

const AddPodOrder: React.FC = observer(() => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { podOrderStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();

  const IMPORT_STEPS = [
    "Download the CSV template and fill in every required column.",
    "Check that every row is correct before uploading.",
    "Import only after the whole sheet is valid — no orders are created if any row has an error.",
  ];

  const handleCsvFileSelect = (files: File[]) => {
    const file = files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      showError("Invalid file", "Please upload a CSV file.");
      return;
    }

    podOrderStore.setPodOrderCsvFile(file);
  };

  const handleCsvRemove = () => {
    podOrderStore.clearPodOrderCsvFile();
  };

  const handleDownloadCsvTemplate = () => {
    const link = document.createElement("a");
    link.href =
      "https://content.swageazy.com/templates/Sample_POD_Orders_Template_New.csv";
    link.download = "Sample_POD_Orders.csv";
    link.rel = "noopener noreferrer";
    link.click();
  };

  const applyImportResponse = (payload?: APIResponse | null) => {
    const importResult = parsePodOrderCsvImportResult(payload?.result);
    const issues = getPodOrderCsvImportIssues(importResult);

    if (issues.length > 0) {
      if (importResult) {
        podOrderStore.setPodOrderCsvImportResult(importResult);
      }
      return;
    }

    if (!payload?.success) {
      podOrderStore.clearPodOrderCsvImportResult();
      showError("Error", payload?.message || "Failed to upload POD Orders.");
      return;
    }

    podOrderStore.clearPodOrderCsvImportResult();
    showSuccess(payload?.message || "POD Orders uploaded successfully.");
    podOrderStore.clearPodOrderCsvFile();
    navigate("/pod-orders/allocated-orders/listing");
  };

  const handleUpload = () => {
    if (!podOrderStore.podOrderCsvFile) {
      showError("Validation error", "Please upload a CSV file.");
      return;
    }

    podOrderStore.clearPodOrderCsvImportResult();
    podOrderStore
      .uploadPodOrdersFromCSV(podOrderStore.podOrderCsvFile)
      .then((response) => {
        applyImportResponse(response);
      })
      .catch((error) => {
        const data = error?.response?.data as APIResponse | undefined;
        if (data) {
          applyImportResponse(data);
          return;
        }
        podOrderStore.clearPodOrderCsvImportResult();
        showError("Error", error?.message || "Failed to upload POD Orders.");
      });
  };

  const csvImportIssues = getPodOrderCsvImportIssues(
    podOrderStore.podOrderCsvImportResult,
  );

  if (!userStore.hasPermission([EmployeeRoles.ADD_POD_ORDER])) {
    return <PermissionBlockLabel />;
  }

  return (
    <Grid2 size={{ xs: 12 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "4px",
          mt: "0.6rem",
          minHeight: { xs: "60vh", md: window.innerHeight - 110 },
          boxShadow:
            "rgba(0,0,0,0.25) 0 0.0625em 0.0625em, rgba(0,0,0,0.25) 0 0.125em 0.5em, rgba(255,255,255,0.1) 0 0 0 1px inset",
        }}
      >
        <Box sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 2.5, md: 3 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    backgroundColor: theme.palette.primary.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <CloudUploadOutlined
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontSize: 24,
                    }}
                  />
                </Box>
                <Box>
                  <CustomTypography variant="heading3">
                    Add Pod Order
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.customColors?.greyTextColor}
                    sx={{ mt: 0.5, maxWidth: 640 }}
                  >
                    Import multiple POD Orders at once with a CSV file and send
                    them into the allocation workflow.
                  </CustomTypography>
                </Box>
              </Box>
            </Box>

            <AppButton
              variant="grayscale"
              label="Download CSV Template"
              startIcon={<DownloadOutlined />}
              onClick={handleDownloadCsvTemplate}
              customStyles={{ minWidth: 210, height: 40, minHeight: 40 }}
            />
          </Box>

          <Grid2 container spacing={2.5} sx={{ mt: 2.5 }}>
            <Grid2 size={{ xs: 12, sm: 5 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor: "divider",
                  overflow: "hidden",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor:
                      theme.palette.customColors?.lightGrayBackground,
                  }}
                >
                  <CustomTypography variant="heading6">
                    Import guide
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Use the template format below before uploading your file.
                  </CustomTypography>
                </Box>

                <Box sx={{ px: 2.5, py: 2 }}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
                  >
                    {IMPORT_STEPS.map((step, index) => (
                      <Box
                        key={step}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.25,
                          p: 1.5,
                          borderRadius: "10px",
                          backgroundColor:
                            theme.palette.customColors?.lightPurple ??
                            theme.palette.action.selected,
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "999px",
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <CustomTypography variant="body2">
                          {step}
                        </CustomTypography>
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: "10px",
                      border: "1px solid",
                      borderColor: theme.palette.customColors?.lightGray,
                      backgroundColor: theme.palette.customColors?.white,
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        fontSize: 18,
                        color: theme.palette.customColors?.grayText,
                        mt: 0.15,
                      }}
                    />
                    <CustomTypography variant="body3" color="text.secondary">
                      Only `.csv` files up to 10MB are supported. Keep the
                      header row unchanged and add one row per pod order line.
                      If any row is invalid, nothing is imported until the whole
                      sheet is fixed. Use a printing type from the list below.
                    </CustomTypography>
                  </Box>
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 7 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor: "divider",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor:
                      theme.palette.customColors?.lightGrayBackground,
                  }}
                >
                  <CustomTypography variant="heading6">
                    Upload CSV
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    Drop your completed file here or browse from your device.
                  </CustomTypography>
                </Box>

                <Box sx={{ px: 2.5, py: 2.5, flex: 1 }}>
                  <ImageUpload
                    size="large"
                    showImagesList={false}
                    uploadText="Drop CSV file here or click to browse"
                    addImagesToBeUploaded={handleCsvFileSelect}
                    removeImage={handleCsvRemove}
                    allowedFileTypes=".csv"
                    fileInfo="Maximum file size: 10MB. Use the template format."
                    newImageList={
                      podOrderStore.podOrderCsvFile
                        ? [podOrderStore.podOrderCsvFile]
                        : []
                    }
                    disabled={podOrderStore.uploadingPodOrders}
                  />

                  {podOrderStore.podOrderCsvFile && (
                    <Box
                      sx={{
                        mt: 2,
                        px: 2,
                        py: 1.5,
                        borderRadius: "12px",
                        border: "1px solid",
                        borderColor: theme.palette.customColors?.lightGray,
                        backgroundColor: theme.palette.customColors?.white,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "10px",
                          backgroundColor:
                            theme.palette.customColors?.lightPurple ??
                            theme.palette.action.selected,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <DescriptionOutlined
                          sx={{ color: theme.palette.primary.main }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <CustomTypography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {podOrderStore.podOrderCsvFile.name}
                        </CustomTypography>
                        <CustomTypography
                          variant="body3"
                          color="text.secondary"
                          sx={{ mt: 0.25 }}
                        >
                          {podOrderStore.podOrderCsvFile.size}
                          {csvImportIssues.length > 0
                            ? " • Import blocked — fix the sheet"
                            : " • Ready to import"}
                        </CustomTypography>
                      </Box>
                      {csvImportIssues.length > 0 ? (
                        <ErrorOutline
                          sx={{
                            color: theme.palette.error.main,
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <CheckCircleOutline
                          sx={{
                            color: theme.palette.customColors?.successGreen,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        aria-label="Remove uploaded file"
                        disabled={podOrderStore.uploadingPodOrders}
                        onClick={handleCsvRemove}
                        sx={{
                          flexShrink: 0,
                          ml: 0.5,
                          color: theme.palette.customColors?.greyTextColor,
                          "&:hover": {
                            color: theme.palette.error.main,
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  {csvImportIssues.length > 0 && (
                    <Alert
                      severity="error"
                      icon={<ErrorOutline fontSize="inherit" />}
                      sx={{
                        mt: 2,
                        borderRadius: "12px",
                        alignItems: "flex-start",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={700}
                        sx={{ mb: 0.5 }}
                      >
                        No orders were imported
                      </CustomTypography>
                      <CustomTypography variant="body2" sx={{ mb: 1 }}>
                        Fix every issue below, save the CSV, and upload again.
                        Nothing is created until all rows are correct.
                      </CustomTypography>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.25,
                          maxHeight: 240,
                          overflowY: "auto",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                        }}
                      >
                        {csvImportIssues.map((issue, index) => (
                          <Box
                            component="li"
                            key={`${issue.row ?? "sheet"}-${issue.cart_id ?? "none"}-${index}`}
                            sx={{ pl: 0 }}
                          >
                            <CustomTypography variant="body2">
                              {formatPodOrderCsvImportIssue(issue)}
                            </CustomTypography>
                          </Box>
                        ))}
                      </Box>
                    </Alert>
                  )}
                </Box>

                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.5,
                    flexWrap: "wrap",
                    backgroundColor: theme.palette.customColors?.white,
                  }}
                >
                  <AppButton
                    variant="secondary"
                    label="Cancel"
                    disabled={podOrderStore.uploadingPodOrders}
                    onClick={() =>
                      navigate("/pod-orders/allocated-orders/listing")
                    }
                    customStyles={{ minWidth: 120 }}
                  />
                  <AppButton
                    variant="primary"
                    label={
                      podOrderStore.uploadingPodOrders
                        ? "Uploading…"
                        : "Import POD Orders"
                    }
                    disabled={
                      !podOrderStore.podOrderCsvFile ||
                      podOrderStore.uploadingPodOrders
                    }
                    startIcon={<FileUploadOutlined />}
                    onClick={handleUpload}
                    customStyles={{ minWidth: 180 }}
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: "12px",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ px: 2.5, py: 1.5 }}>
                  <CustomTypography variant="heading6">
                    Allowed printing types
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25 }}
                  >
                    Use these values in the Printing Type column of your sheet.
                  </CustomTypography>
                  <Box
                    sx={{
                      mt: 1.25,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {POD_ALLOWED_PRINTING_TYPES.map((item) => (
                      <PodOrderPrintingTypeBadge
                        key={item.name}
                        name={item.name}
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        </Box>
      </Paper>
    </Grid2>
  );
});

export default AddPodOrder;
